import { Box } from '@material-ui/core';
import { useState } from 'react';
import type { PairingPurpose } from '@internal/spassu-code-contract';
import { DENIED_CAPABILITY } from '../hooks/useControlPlaneManagement';
import {
  PrimaryConnectionActions,
  TerminalActivityActions,
  WorkspaceCleanupActions,
  type ManagementActionProps,
} from './ControlPlaneActionGroups';
import {
  PairingIntentDialog,
  ResumeAttemptDialog,
} from './ControlPlanePairingDialogs';
import { ControlPlaneCancelDialog } from './ControlPlaneCancelDialog';
import { ControlPlaneUsageRecoveryPanel } from './ControlPlaneUsageRecoveryPanel';
import { useConnectionPageStyles } from './useConnectionPageStyles';

function useActionConfirmations(props: ManagementActionProps) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pairingIntent, setPairingIntent] = useState<PairingPurpose | null>(
    null,
  );
  const [resumeOpen, setResumeOpen] = useState(false);
  return {
    cancelOpen,
    pairingIntent,
    resumeOpen,
    onCancel: () => setCancelOpen(true),
    closeCancel: () => setCancelOpen(false),
    confirmCancel: (reason: string) => {
      setCancelOpen(false);
      void props.management.cancel(reason);
    },
    onPair: (purpose: PairingPurpose) => setPairingIntent(purpose),
    closePair: () => setPairingIntent(null),
    confirmPair: (purpose: PairingPurpose) => {
      setPairingIntent(null);
      void props.onboarding.generate(purpose);
    },
    onResume: () => setResumeOpen(true),
    closeResume: () => setResumeOpen(false),
    confirmResume: () => {
      setResumeOpen(false);
      const sessionId = props.management.projection?.attempt?.sessionId;
      if (sessionId) void props.onboarding.resume(sessionId);
    },
  };
}

function ActionConfirmations(props: {
  dialogs: ReturnType<typeof useActionConfirmations>;
  activityTitle: string;
  busy: boolean;
}) {
  const { dialogs, activityTitle, busy } = props;
  return (
    <>
      <PairingIntentDialog
        purpose={dialogs.pairingIntent}
        busy={busy}
        onClose={dialogs.closePair}
        onConfirm={dialogs.confirmPair}
      />
      <ResumeAttemptDialog
        open={dialogs.resumeOpen}
        busy={busy}
        onClose={dialogs.closeResume}
        onConfirm={dialogs.confirmResume}
      />
      <ControlPlaneCancelDialog
        open={dialogs.cancelOpen}
        activityTitle={activityTitle}
        busy={busy}
        onClose={dialogs.closeCancel}
        onConfirm={dialogs.confirmCancel}
      />
    </>
  );
}

/** Capabilities control visibility; dialog state never authorizes a request. */
export function ManagementActions(props: ManagementActionProps) {
  const classes = useConnectionPageStyles();
  const dialogs = useActionConfirmations(props);
  const capabilities = props.management.projection?.capabilities;
  const busy =
    props.management.operation !== 'idle' ||
    props.onboarding.operation !== 'idle';
  const actions = {
    ...props,
    busy,
    onPair: dialogs.onPair,
    onResume: dialogs.onResume,
    onCancel: dialogs.onCancel,
  };
  return (
    <Box className={classes.section} data-testid="management-actions">
      <PrimaryConnectionActions {...actions} />
      <TerminalActivityActions {...actions} />
      <WorkspaceCleanupActions {...actions} />
      <ControlPlaneUsageRecoveryPanel
        management={props.management}
        capability={capabilities?.recoverUsage ?? DENIED_CAPABILITY}
        activityTitle={props.activityTitle}
        busy={busy}
      />
      <ActionConfirmations
        dialogs={dialogs}
        activityTitle={props.activityTitle}
        busy={busy}
      />
    </Box>
  );
}
